import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const UserList = () => {
  const [data, setData] = useState([]);
  const [UserId, setUserId] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [publishInfo, setPublishInfo] = useState({}); // State to hold publish info
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [masterPassword, setMasterPassword] = useState("");
  const [userPassword, setUserPassword] = useState("");
  
  // Pagination states
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  
  // Merchant allocation states
  const [showMerchantAllocationModal, setShowMerchantAllocationModal] = useState(false);
  const [selectedUserForAllocation, setSelectedUserForAllocation] = useState(null);
  const [allocatedMerchants, setAllocatedMerchants] = useState([]);
  const [unallocatedMerchants, setUnallocatedMerchants] = useState([]);
  const [allMerchants, setAllMerchants] = useState([]);
  const [originalAllocatedMerchants, setOriginalAllocatedMerchants] = useState([]); // Track original state
  const [draggedItem, setDraggedItem] = useState(null);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false); // New loading state for save operation
  
  // Bulk allocation states
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);

  // NonAssign data state (used in POS allocation modal)
  const [nonAssignData, setNonAssignData] = useState([]);
  const [loadingNonAssign, setLoadingNonAssign] = useState(false);

  const { getUrlByOperation } = useApiUrls();
  const navigate = useNavigate();
  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch catalog and User data
  useEffect(() => {
    const fetchCatalogAndUserData = async () => {
      try {
        const requestBody = {
          id: 0,
          applycreateddate: false,
          applymodifieddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          frommodifieddate: 0,
          endmodifieddate: 0,
          active: "",
          catalogname: "",
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "first",
        };
        
        const SubUserSearch = getUrlByOperation("SubUserSearch");

        // Fetch user data
        const userResponse = await axios.post(SubUserSearch, requestBody, {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
          },
        });
        if (userResponse.data && Array.isArray(userResponse.data.data)) {
          const rows = userResponse.data.data;
          setData(rows);
          setHasMore(rows.length === Number(dataLimit));
          const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
          setTotalItems(approximateTotal);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchCatalogAndUserData();
  }, [getUrlByOperation, token, userid, dataLimit, count]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleMerchantAllocation = async (user) => {
    setSelectedUserForAllocation(user);
    setShowMerchantAllocationModal(true);
    
    const [allocatedData, nonAssignedData] = await Promise.all([
      fetchPOSData(user.id),
      fetchNonAssignData()
    ]);
    
    setOriginalAllocatedMerchants(allocatedData);
  };

  const fetchPOSData = async (userId) => {
    setLoadingMerchants(true);
    try {
      const requestBody = { id: userId || 2 }; // Use the provided user ID or default to 2
      
      
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserPOSOperation/GetByUser',
        requestBody,
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );


      if (response.data && response.data.isresponse && response.data.data) {
        // Transform API data to match our expected format
        const transformedData = response.data.data.map(item => ({
          id: item.id, // This is the SubUserPOSOperation ID used for delete
          posid: item.posid,
          merchantname: item.merchantid || `POS ${item.posid}`,
          name: item.posname || `POS ${item.posid}`,
          apikey: item.apikey,
          active: item.active !== null ? item.active : true,
          userid: item.userid // Store user ID for reference
        }));
        
        setAllMerchants(transformedData);
        return transformedData;
      }
      return [];
    } catch (error) {
      console.error("Error fetching POS data:", error);
      setMessage("Failed to fetch POS data");
      return [];
    } finally {
      setLoadingMerchants(false);
    }
  };

  const fetchNonAssignData = async () => {
    setLoadingNonAssign(true);
    try {
     
      const response = await axios.get(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserPOSOperation/Get/NonAssign',
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
          }
        }
      );


      if (response.data && response.data.isresponse && response.data.data) {
        // Transform NonAssign data to match our expected format
        const transformedNonAssignData = response.data.data.map(item => ({
          id: item.id, // POS ID
          posid: item.id,
          merchantname: item.posname || `POS ${item.id}`,
          name: item.posname || `POS ${item.id}`,
          apikey: item.apikey || '',
          active: item.active !== null ? item.active : true
        }));
        
        setNonAssignData(transformedNonAssignData);
        return transformedNonAssignData;
      } else {
        setNonAssignData([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching NonAssign data:", error);
      setMessage("Failed to fetch NonAssign POS data");
      setNonAssignData([]);
      return [];
    } finally {
      setLoadingNonAssign(false);
    }
  };

  const handleAddPop = () => {
    navigate("/addUser");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updateuser/${id}`);
  };

  const handleShowPassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
    setMasterPassword("");
    setUserPassword("");
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setMasterPassword("");
    setUserPassword("");
  };

  const handleShowUserPassword = async () => {
    if (!masterPassword.trim()) {
      setMessage("Please enter master password");
      return;
    }

    try {
      const SubUserShowPassword = getUrlByOperation("SubUserShowPassword");
      const requestBody = {
        id: selectedUser.id,
        password: masterPassword
      };

      const response = await axios.post(SubUserShowPassword, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          "accept": "text/plain",
        },
      });

      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        setUserPassword(response.data.password);
        setMessage("Password retrieved successfully!");
        setMessageType("success");
      } else {
        setMessage("Failed to retrieve password. Please check master password.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // const handleDelete = async (id) => {
  //   const requestBody = { id };

  //   try {
  //     const SubUserDelete = getUrlByOperation("SubUserDelete");
  //     await axios.post(SubUserDelete, requestBody, {
  //       headers: {
  //         "X-Authorization": token,
  //         UserId: userid,
  //         "Content-Type": "application/json-patch+json",
  //       },
  //     });
  //     setMessage("User deleted successfully!");
  //     setData(data.filter((item) => item.id !== id));
  //   } catch (error) {
  //     console.error("Error deleting user:", error);
  //     setMessage("Failed to delete user");
  //   }
  // };

  // const handleToggle = async (item) => {
  //   const SubUserActive = getUrlByOperation("SubUserActive");
  //   const SubUserDeactive = getUrlByOperation("SubUserDeactive");
  //   const apiUrl = item.active
  //     ? SubUserDeactive
  //     : SubUserActive;

  //   const requestBody = { id: item.id };

  //   try {
  //     await axios.post(apiUrl, requestBody, {
  //       headers: {
  //         "X-Authorization": token,
  //         UserId: userid,
  //         "Content-Type": "application/json-patch+json",
  //       },
  //     });
  //     setData(
  //       data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d))
  //     );
  //     setMessage(`User ${item.active ? 'deactivated' : 'activated'} successfully!`);
  //   } catch (error) {
  //     console.error("Error updating user status:", error);
  //     setMessage("Failed to update user status");
  //     if (error.message === "relogin token expire") {
  //       navigate("/login");
  //     }
  //   }
  // };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Update allocated and unallocated merchants when data changes and modal is open
  useEffect(() => {
    if (showMerchantAllocationModal) {
      // Put user's assigned POS in allocated section
      if (allMerchants.length > 0) {
        setAllocatedMerchants(allMerchants);
      }
      
      // Put non-assigned POS in unallocated section
      if (nonAssignData.length > 0) {
        setUnallocatedMerchants(nonAssignData);
      }
    }
  }, [allMerchants, nonAssignData, showMerchantAllocationModal]);

  const handleCloseMerchantAllocationModal = () => {
    setShowMerchantAllocationModal(false);
    setSelectedUserForAllocation(null);
    setAllocatedMerchants([]);
    setUnallocatedMerchants([]);
    setOriginalAllocatedMerchants([]);
    setDraggedItem(null);
    setBulkFile(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      setMessage("Please select a file first");
      return;
    }

    setUploadingBulk(true);
    try {
      const formData = new FormData();
      formData.append("SubUserId", selectedUserForAllocation.id);
      formData.append("file", bulkFile);

      const response = await axios.post(
        "https://api.dqr-display-tms.bonrix.in/api/SubUserPOSOperation/Upload",
        formData,
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        const { totalrawsuccess, totalrawfail, failids } = response.data;
        
        if (totalrawfail > 0) {
          const failMessages = failids.map(f => `Line ${f.id}: ${f.msg}`).join(", ");
          setMessage(`Partial success. OK: ${totalrawsuccess}, Fail: ${totalrawfail}. Errors: ${failMessages}`);
          setMessageType("warning");
        } else {
          setMessage(`Bulk allocation successful! Processed ${totalrawsuccess} records.`);
          setMessageType("success");
        }
        
        setBulkFile(null);
        // Reset file input
        const fileInput = document.getElementById('bulkAllocationFile');
        if (fileInput) fileInput.value = '';
        
        // Refresh data
        fetchPOSData(selectedUserForAllocation.id);
        fetchNonAssignData();
      } else {
        setMessage(response.data?.errormsg || "Bulk allocation failed");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      setMessage("Error during bulk allocation");
      setMessageType("error");
    } finally {
      setUploadingBulk(false);
    }
  };

  const handleDownloadSampleAllocation = async () => {
    try {
      // Load XLSX if not available
      if (window.XLSX === undefined) {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js";
        document.head.appendChild(script);
        await new Promise((resolve) => (script.onload = resolve));
      }

      const sampleData = [
        ["posname", "merchantid", "key"],
        ["checkP1", "checkM1", "checkK1"],
        ["checkP2", "checkM2", "checkK2"],
        ["checkP3", "checkM3", "checkK3"],
        ["checkP4", "checkM4", "checkK4"],
        ["checkP5", "checkM5", "checkK5"],
        ["checkP6", "checkM6", "checkK6"],
      ];

      const worksheet = window.XLSX.utils.aoa_to_sheet(sampleData);
      const workbook = window.XLSX.utils.book_new();
      window.XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
      window.XLSX.writeFile(workbook, "BulkAllocationSample.xlsx");
    } catch (error) {
      console.error("Error generating sample:", error);
      setMessage("Error generating sample file");
      setMessageType("error");
    }
  };

  const handleDragStart = (e, item, source) => {
    setDraggedItem({ item, source });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, target) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    const { item, source } = draggedItem;
    
    if (source === target) return;

    if (source === 'allocated' && target === 'unallocated') {
      // Move from allocated to unallocated
      setAllocatedMerchants(prev => prev.filter(merchant => merchant.posid !== item.posid));
      setUnallocatedMerchants(prev => [...prev, item]);
    } else if (source === 'unallocated' && target === 'allocated') {
      // Move from unallocated to allocated
      setUnallocatedMerchants(prev => prev.filter(merchant => merchant.posid !== item.posid));
      setAllocatedMerchants(prev => [...prev, item]);
    }

    setDraggedItem(null);
  };

  // Insert POS allocation API call
  const insertPOSAllocation = async (userId, posId) => {
    try {
      const requestBody = {
        subuserid: parseInt(userId),
        posid: parseInt(posId)
      };

      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserPOSOperation/Insert',
        requestBody,
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error inserting POS allocation:', error);
      throw error;
    }
  };

  // Delete POS allocation API call
  const deletePOSAllocation = async (subUserPOSOperationId) => {
    try {
      const requestBody = {
        id: subUserPOSOperationId
      };

      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserPOSOperation/Delete',
        requestBody,
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting POS allocation:', error);
      throw error;
    }
  };

  const handleSaveAllocation = async () => {
    setSavingAllocation(true);
    try {
      const userId = selectedUserForAllocation.id;
      
      // Find newly allocated POS (in current allocated but not in original)
      const newlyAllocated = allocatedMerchants.filter(current => 
        !originalAllocatedMerchants.some(original => original.posid === current.posid)
      );
      
      // Find newly unallocated POS (in original but not in current allocated)
      const newlyUnallocated = originalAllocatedMerchants.filter(original => 
        !allocatedMerchants.some(current => current.posid === original.posid)
      );

      const insertPromises = [];
      const deletePromises = [];

      // Create insert requests for newly allocated POS
      for (const pos of newlyAllocated) {
        insertPromises.push(insertPOSAllocation(userId, pos.posid));
      }

      // Create delete requests for newly unallocated POS
      for (const pos of newlyUnallocated) {
        // Use the SubUserPOSOperation ID for deletion
        deletePromises.push(deletePOSAllocation(pos.id));
      }

      // Execute all API calls
      const results = await Promise.allSettled([
        ...insertPromises,
        ...deletePromises
      ]);

      // Check results
      const failedOperations = results.filter(result => result.status === 'rejected');
      
      if (failedOperations.length === 0) {
        setMessage(`POS allocation updated successfully! Allocated: ${newlyAllocated.length} new POS, Unallocated: ${newlyUnallocated.length} POS`);
        setMessageType("success");
        handleCloseMerchantAllocationModal();
      } else {
        console.error('Some operations failed:', failedOperations);
        setMessage(`POS allocation partially updated. ${failedOperations.length} operations failed.`);
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error updating POS allocation:", error);
      setMessage("Failed to update POS allocation");
      setMessageType("error");
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleDelete = async (id) => {
    const requestBody = { id };

    try {
      const SubUserDelete = getUrlByOperation("SubUserDelete");
      await axios.post(SubUserDelete, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setMessage("User deleted successfully!");
      setMessageType("success");
      setData(data.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage("Failed to delete user");
      setMessageType("error");
    }
  };

  const handleToggle = async (item) => {
    const SubUserActive = getUrlByOperation("SubUserActive");
    const SubUserDeactive = getUrlByOperation("SubUserDeactive");
    const apiUrl = item.active
      ? SubUserDeactive
      : SubUserActive;

    const requestBody = { id: item.id };

    try {
      await axios.post(apiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      setData(
        data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d))
      );
      setMessage(`User ${item.active ? 'deactivated' : 'activated'} successfully!`);
      setMessageType("success");
    } catch (error) {
      console.error("Error updating user status:", error);
      setMessage("Failed to update user status");
      setMessageType("error");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Update allocated and unallocated merchants when data changes and modal is open
  useEffect(() => {
    if (showMerchantAllocationModal) {
      // Put user's assigned POS in allocated section
      if (allMerchants.length > 0) {
        setAllocatedMerchants(allMerchants);
      }
      
      // Put non-assigned POS in unallocated section
      if (nonAssignData.length > 0) {
        setUnallocatedMerchants(nonAssignData);
      }
    }
  }, [allMerchants, nonAssignData, showMerchantAllocationModal]);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">User</h1>
                <button type="button" className="btn btn-success" onClick={handleAddPop}>
                  Add User
                </button>
              </div>
            </div>
          </section>
          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                         <thead style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                          <tr>
                            <th style={{width: '5%', color: 'var(--text)', borderColor: 'var(--border)' }} className="text-center">Id</th>
                            <th style={{width: '15%', color: 'var(--text)', borderColor: 'var(--border)' }}>Name</th>
                            <th style={{width: '10%', color: 'var(--text)', borderColor: 'var(--border)' }} className="text-center">Active</th>
                            <th style={{width: '15%', color: 'var(--text)', borderColor: 'var(--border)' }} className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item) => (
                            <tr key={item.id}>
                              <td className="text-center">{item.id}</td>
                              <td>
                                <strong>{item.username}</strong>
                              </td>
                              <td className="text-center">
                                <Switch 
                                  isOn={item.active} 
                                  handleToggle={() => handleToggle(item)} 
                                  id={item.id} 
                                />
                              </td>
                              <td className="text-center">
                                <button 
                                  className="btn btn-sm btn-info mr-1"
                                  onClick={() => handleShowPassword(item)}
                                  title="Show Password"
                                >
                                  Show Password
                                </button>
                                <button 
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleMerchantAllocation(item)}
                                  title="POS Allocation"
                                >
                                  POS allocation
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <Pagination
                        currentPage={count + 1}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={dataLimit}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalItems}
                        showPageNumbers={false}
                        showItemsPerPageSelector={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${
              messageType === "success" ? "alert-success" : 
              messageType === "warning" ? "alert-warning" : 
              "alert-danger"
            } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}

      {/* Show Password Modal */}
      {showPasswordModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Show Password - {selectedUser?.username}</h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={handleClosePasswordModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="masterPassword">Master Password:</label>
                  <input
                    type="password"
                    className="form-control"
                    id="masterPassword"
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    placeholder="Enter master password"
                  />
                </div>
                
                {userPassword && (
                  <div className="form-group">
                    <label htmlFor="userPassword">User Password:</label>
                    <input
                      type="text"
                      className="form-control"
                      id="userPassword"
                      value={userPassword}
                      readOnly
                      style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleClosePasswordModal}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleShowUserPassword}
                  disabled={!masterPassword.trim()}
                >
                  Show Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merchant Allocation Modal */}
      {showMerchantAllocationModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl" style={{ maxWidth: '90%', margin: '20px auto' }}>
            <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header" style={{ flexShrink: 0 }}>
                <h5 className="modal-title">POS Allocation - {selectedUserForAllocation?.username}</h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={handleCloseMerchantAllocationModal}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: '20px' }}>
                {loadingMerchants || loadingNonAssign ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading POS data...</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Bulk Upload Section */}
                    <div className="card mb-3" style={{ flexShrink: 0, border: '1px solid #17a2b8' }}>
                      <div className="card-header bg-info text-white py-2">
                        <h6 className="mb-0"><i className="fas fa-file-excel mr-2"></i>Bulk Allocation via Excel</h6>
                      </div>
                      <div className="card-body py-3">
                        <div className="row align-items-end">
                          <div className="col-md-6">
                            <label className="small font-weight-bold">Select Excel File:</label>
                            <input 
                              type="file" 
                              id="bulkAllocationFile"
                              className="form-control form-control-sm" 
                              accept=".xlsx, .xls"
                              onChange={(e) => setBulkFile(e.target.files[0])}
                            />
                          </div>
                          <div className="col-md-6">
                            <button 
                              className="btn btn-sm btn-info mr-2"
                              onClick={handleBulkUpload}
                              disabled={!bulkFile || uploadingBulk}
                            >
                              {uploadingBulk ? (
                                <><span className="spinner-border spinner-border-sm mr-1"></span>Uploading...</>
                              ) : (
                                <><i className="fas fa-upload mr-1"></i>Bulk Upload</>
                              )}
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-info"
                              onClick={handleDownloadSampleAllocation}
                            >
                              <i className="fas fa-download mr-1"></i>Sample Excel
                            </button>
                          </div>
                        </div>
                        <div className="mt-2">
                          <small className="text-muted">
                            Columns required: <strong>posname</strong>, <strong>merchantid</strong>, <strong>key</strong>
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="row" style={{ flex: 1, overflow: 'hidden' }}>
                      {/* Unallocated POS */}
                      <div className="col-md-6" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <h6 className="text-center mb-3" style={{ color: '#dc3545', flexShrink: 0 }}>
                          Unallocated POS ({unallocatedMerchants.length})
                        </h6>
                        <div 
                          className="border rounded p-3" 
                          style={{ 
                            flex: 1,
                            backgroundColor: '#f8f9fa',
                            borderColor: '#dc3545',
                            overflowY: 'auto',
                            maxHeight: '400px'
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, 'unallocated')}
                        >
                          {unallocatedMerchants.map((pos) => (
                            <div
                              key={pos.posid}
                              className="border rounded p-2 mb-2 cursor-pointer"
                              style={{ 
                                backgroundColor: 'white',
                                cursor: 'grab',
                                transition: 'all 0.2s ease'
                              }}
                              draggable
                              onDragStart={(e) => handleDragStart(e, pos, 'unallocated')}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{pos.posname || pos.name || `POS ${pos.posid}`}</strong>
                                  <br />
                                  <small className="text-muted">POS ID: {pos.posid || pos.id}</small>
                                  {pos.apikey && (
                                    <>
                                      <br />
                                      <small className="text-muted">API Key: {pos.apikey}</small>
                                    </>
                                  )}
                                </div>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </div>
                            </div>
                          ))}
                          {unallocatedMerchants.length === 0 && (
                            <div className="text-center text-muted mt-5">
                              <i className="fas fa-inbox fa-2x mb-2"></i>
                              <p>No unallocated POS</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Allocated POS */}
                      <div className="col-md-6" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <h6 className="text-center mb-3" style={{ color: '#28a745', flexShrink: 0 }}>
                          Allocated POS ({allocatedMerchants.length})
                        </h6>
                        <div 
                          className="border rounded p-3" 
                          style={{ 
                            flex: 1,
                            backgroundColor: '#f8f9fa',
                            borderColor: '#28a745',
                            overflowY: 'auto',
                            maxHeight: '400px'
                          }}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, 'allocated')}
                        >
                          {allocatedMerchants.map((pos) => (
                            <div
                              key={pos.posid}
                              className="border rounded p-2 mb-2 cursor-pointer"
                              style={{ 
                                backgroundColor: 'white',
                                cursor: 'grab',
                                transition: 'all 0.2s ease'
                              }}
                              draggable
                              onDragStart={(e) => handleDragStart(e, pos, 'allocated')}
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{pos.posname || pos.name || `POS ${pos.posid}`}</strong>
                                  <br />
                                  <small className="text-muted">POS ID: {pos.posid || pos.id}</small>
                                  {pos.apikey && (
                                    <>
                                      <br />
                                      <small className="text-muted">API Key: {pos.apikey}</small>
                                    </>
                                  )}
                                </div>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </div>
                            </div>
                          ))}
                          {allocatedMerchants.length === 0 && (
                            <div className="text-center text-muted mt-5">
                              <i className="fas fa-inbox fa-2x mb-2"></i>
                              <p>No allocated POS</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3" style={{ flexShrink: 0 }}>
                      <div className="alert alert-info d-flex align-items-center">
                        <i className="fas fa-info-circle me-2"></i>
                        <small>
                          Drag and drop POS between the lists to allocate or deallocate them. 
                          Use the scroll bars to view all POS.
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ flexShrink: 0, borderTop: '1px solid #dee2e6', padding: '15px 20px' }}>
                <div className="d-flex justify-content-between w-100">
                  <div>
                    <small className="text-muted">
                      Total: {allocatedMerchants.length + unallocatedMerchants.length} POS
                    </small>
                  </div>
                  <div>
                    <button 
                      type="button" 
                      className="btn btn-secondary me-2" 
                      onClick={handleCloseMerchantAllocationModal}
                      disabled={savingAllocation}
                    >
                      <i className="fas fa-times me-1"></i>
                      Close
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-success" 
                      onClick={handleSaveAllocation}
                      disabled={savingAllocation}
                    >
                      {savingAllocation ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-1"></i>
                          Save Allocation
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserList;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}